"use client";

import { useState } from "react";
import {
  MediaBufferingIndicator,
  MediaFullscreenButton,
  MediaMuteButton,
  MediaOutlet,
  MediaPIPButton,
  MediaPlayer,
  MediaPlayButton,
  MediaPoster,
  MediaTime,
  MediaTimeSlider,
  MediaTooltip,
} from "@vidstack/react";
import {
  Maximize2,
  Minimize2,
  Pause,
  PictureInPicture2,
  Play,
  RefreshCw,
  RotateCcw,
  Volume1,
  Volume2,
  VolumeX,
} from "lucide-react";
import { cn } from "~/lib/utils";

interface VideoPlayerProps {
  src: string;
  title: string;
  autoPlay?: boolean;
  className?: string;
  aspectRatio?: number;
  poster?: string;
  posterAlt?: string;
  contextLabel?: string;
}

/**
 * The shared, accessible playback surface for on-demand video. Keep capture and
 * upload previews on the native element because they require direct media access.
 */
export function VideoPlayer({
  src,
  title,
  autoPlay = false,
  className,
  aspectRatio,
  poster,
  posterAlt = "",
  contextLabel,
}: VideoPlayerProps) {
  const [hasError, setHasError] = useState(false);
  const [retryKey, setRetryKey] = useState(0);
  const videoSrc = { src, type: "video/mp4" };

  const retry = () => {
    setHasError(false);
    setRetryKey((currentKey) => currentKey + 1);
  };

  return (
    <MediaPlayer
      key={retryKey}
      aria-label={title}
      aspectRatio={aspectRatio}
      autoPlay={autoPlay}
      className={cn("resimo-video-player", className)}
      load="eager"
      onError={() => setHasError(true)}
      poster={poster}
      posterLoad="eager"
      src={videoSrc}
      streamType="on-demand"
      title={title}
    >
      <MediaOutlet />
      {poster && <MediaPoster alt={posterAlt} />}
      <MediaBufferingIndicator className="resimo-video-player__buffering" />
      <div aria-hidden="true" className="resimo-video-player__loading-copy">
        Učitavanje videa…
      </div>
      {contextLabel && (
        <div aria-hidden="true" className="resimo-video-player__context">
          {contextLabel}
        </div>
      )}
      {hasError && (
        <div className="resimo-video-player__error" role="status">
          <p>Video trenutno nije moguće učitati.</p>
          <button type="button" onClick={retry}>
            <RefreshCw aria-hidden="true" />
            Pokušaj ponovo
          </button>
        </div>
      )}
      <div className="resimo-video-player__controls">
        <MediaPlayButton className="resimo-video-player__center-play">
          <Play aria-hidden="true" slot="play" />
          <Pause aria-hidden="true" slot="pause" />
          <RotateCcw aria-hidden="true" slot="replay" />
        </MediaPlayButton>

        <div className="resimo-video-player__control-bar">
          <MediaTimeSlider
            className="resimo-video-player__timeline"
            trackClass="h-1.5 w-full overflow-hidden rounded-full bg-white/30"
            trackFillClass="h-full bg-brand-decorative"
            trackProgressClass="h-full bg-white/20"
            thumbContainerClass="absolute inset-y-0"
            thumbClass="h-3.5 w-3.5 rounded-full border-2 border-white bg-brand-decorative shadow-xs"
          />

          <div className="flex items-center gap-1 text-sm font-medium text-white">
            <MediaPlayButton className="resimo-video-player__icon-button">
              <Play aria-hidden="true" slot="play" />
              <Pause aria-hidden="true" slot="pause" />
              <RotateCcw aria-hidden="true" slot="replay" />
              <MediaTooltip>
                <span slot="play">Pusti</span>
                <span slot="pause">Pauziraj</span>
                <span slot="replay">Pusti ponovo</span>
              </MediaTooltip>
            </MediaPlayButton>
            <MediaMuteButton className="resimo-video-player__icon-button">
              <Volume2 aria-hidden="true" slot="volume-high" />
              <Volume1 aria-hidden="true" slot="volume-low" />
              <VolumeX aria-hidden="true" slot="volume-muted" />
              <MediaTooltip>
                <span slot="mute">Isključi zvuk</span>
                <span slot="unmute">Uključi zvuk</span>
              </MediaTooltip>
            </MediaMuteButton>
            <MediaTime type="current" />
            <span aria-hidden="true" className="text-white/60">
              /
            </span>
            <MediaTime type="duration" />
            <MediaPIPButton className="resimo-video-player__icon-button ml-auto">
              <PictureInPicture2 aria-hidden="true" slot="enter" />
              <Minimize2 aria-hidden="true" slot="exit" />
              <MediaTooltip>
                <span slot="enter">Slika u slici</span>
                <span slot="exit">Zatvori sliku u slici</span>
              </MediaTooltip>
            </MediaPIPButton>
            <MediaFullscreenButton className="resimo-video-player__icon-button">
              <Maximize2 aria-hidden="true" slot="enter" />
              <Minimize2 aria-hidden="true" slot="exit" />
              <MediaTooltip position="top right">
                <span slot="enter">Ceo ekran</span>
                <span slot="exit">Izađi iz celog ekrana</span>
              </MediaTooltip>
            </MediaFullscreenButton>
          </div>
        </div>
      </div>
    </MediaPlayer>
  );
}
